/**
 * Sum the extra_price of all selected bubble tea option items for a given
 * `selections` map ({ categoryName: value }).
 *
 * `value` is either a single option item name (radio-style categories: Tea
 * Type, Sugar, Ice, Size) or a comma-joined string of multiple option item
 * names (the "Toppings" checkbox category). For each [categoryName, value]
 * entry, an exact whole-string match against the trimmed value is attempted
 * first; only if no match is found do we fall back to splitting on ","
 * and matching each fragment. This exact-match-first order matters because
 * a single-select option item's own name can legitimately contain a comma
 * (e.g. Sugar Level's "Extra, Sweet") — splitting unconditionally would
 * break that name's lookup even though it was never a multi-select value.
 *
 * This mirrors backend/app/api/bubbletea.py::_sum_option_extra_price exactly
 * (match by category name + trimmed option name, exact match before a
 * known-name-aware comma-split fallback) so the frontend-displayed price
 * never diverges from what the backend/Stripe actually charges. Unmatched
 * category/item name combinations, or whose item has been deactivated
 * (active === false), are treated as extra_price 0 and never throw.
 */

import { getLocalizedField } from '../utils/localize';
import type { SupportedLanguage } from '../i18n';
import type { BubbleTeaOptionCategory, BubbleTeaOptionItem } from '../api/adminBubbleTeaOptions';

/**
 * Split a comma-joined multi-select option value (e.g. a Toppings
 * selection) into tokens, preferring known active option-item names
 * (longest name first) as whole tokens bounded by a comma or
 * end-of-string. Used as the fallback path in `computeOptionsExtraPrice`
 * when the whole-string exact match fails — prevents a grandfathered
 * comma-containing item name (e.g. "Pearls, Extra Sweet") from being
 * fragmented by a naive split(",") when selected together with other
 * values (e.g. "Pearls, Extra Sweet,Boba"). Any remaining text that
 * matches no known active name falls back to a raw comma-cut, identical
 * to the old split(",") behavior.
 * Mirrors backend/app/api/bubbletea.py::_split_multi_value_by_known_names.
 */
export const splitMultiValueByKnownNames = (
  value: string,
  items: any[],
  options: { activeOnly?: boolean } = {},
): string[] => {
  const { activeOnly = true } = options;
  const candidates = (items || [])
    .filter((i: any) => (activeOnly ? i.active : true))
    .map((i: any) => i.name)
    .sort((a: string, b: string) => b.length - a.length);

  const tokens: string[] = [];
  let remaining = value.trim();
  while (remaining) {
    const matched = candidates.find(
      (candidate: string) => remaining === candidate || remaining.startsWith(candidate + ','),
    );
    if (matched) {
      tokens.push(matched);
      remaining = remaining.slice(matched.length).replace(/^,/, '').trim();
    } else {
      const commaIndex = remaining.indexOf(',');
      if (commaIndex === -1) {
        tokens.push(remaining);
        remaining = '';
      } else {
        tokens.push(remaining.slice(0, commaIndex));
        remaining = remaining.slice(commaIndex + 1).trim();
      }
    }
  }
  return tokens;
};

// Drops option selections that no longer correspond to an active option item
// (e.g. the item was deactivated after the cart item was originally added).
// Mirrors the modal's own checkbox/radio `checked` logic exactly.
export const sanitizeOptionSelections = (
  selections: Record<string, string>,
  categories: BubbleTeaOptionCategory[],
): Record<string, string> => {
  const result: Record<string, string> = {};
  Object.entries(selections).forEach(([categoryName, value]) => {
    if (!value) return;
    const cat = categories.find((c: BubbleTeaOptionCategory) => c.name === categoryName);
    if (!cat) return;
    const activeNames = new Set((cat.items || []).filter((i: BubbleTeaOptionItem) => i.active).map((i: BubbleTeaOptionItem) => i.name));
    if (cat.selection_mode === 'multiple') {
      const kept = splitMultiValueByKnownNames(value, cat.items).map((t: string) => t.trim()).filter((t: string) => activeNames.has(t));
      if (kept.length > 0) result[categoryName] = kept.join(',');
    } else if (activeNames.has(value)) {
      result[categoryName] = value;
    }
  });
  return result;
};

// Localizes a `selections` map ({ categoryName: value }) into an ordered
// list of { key, value } display pairs, where `key` is the localized
// category name and `value` is the localized selected item name(s).
// Mirrors computeOptionsExtraPrice's exact-match-then-known-name-split-
// fallback matching exactly (see that function's doc comment above) so the
// displayed names never diverge from what's actually being priced/charged.
// A category or item with no live catalog match (deleted/deactivated since
// the order was placed) falls back to the raw stored English name/value,
// same as computeOptionsExtraPrice falls back to extra_price 0 for those.
export const localizeOptionsEntries = (
  selections: Record<string, string> | undefined,
  categories: any[],
  language: SupportedLanguage,
): Array<{ key: string; value: string }> => {
  if (!selections) return [];
  return Object.entries(selections)
    .filter(([, value]) => !!value)
    .map(([categoryName, value]) => {
      const cat = categories.find((c: any) => c.name === categoryName);
      const key = cat ? (getLocalizedField(cat, 'name', language) as string) : categoryName;
      if (!cat) return { key, value };

      const exactMatch = (cat.items || []).find((i: any) => i.name === value.trim());
      if (exactMatch) {
        return { key, value: getLocalizedField(exactMatch, 'name', language) as string };
      }

      const localizedValue = splitMultiValueByKnownNames(value, cat.items, { activeOnly: false })
        .map((raw) => {
          const optionName = raw.trim();
          if (!optionName) return raw;
          const optionItem = (cat.items || []).find((i: any) => i.name === optionName);
          return optionItem ? (getLocalizedField(optionItem, 'name', language) as string) : optionName;
        })
        .join(', ');
      return { key, value: localizedValue };
    });
};

export const computeOptionsExtraPrice = (
  selections: Record<string, string> | undefined,
  categories: any[],
): number => {
  if (!selections) return 0;
  let total = 0;
  Object.entries(selections).forEach(([categoryName, value]) => {
    if (!value) return;
    const cat = categories.find((c: any) => c.name === categoryName);
    if (!cat) return;

    const exactMatch = (cat.items || []).find((i: any) => i.name === value.trim() && i.active);
    if (exactMatch) {
      if (exactMatch.extra_price) total += exactMatch.extra_price;
      return;
    }

    splitMultiValueByKnownNames(value, cat.items).forEach((raw) => {
      const optionName = raw.trim();
      if (!optionName) return;
      const optionItem = (cat.items || []).find((i: any) => i.name === optionName && i.active);
      if (optionItem && optionItem.extra_price) {
        total += optionItem.extra_price;
      }
    });
  });
  return total;
};
